import {DataStatus, FormControl, FormField} from 'platform/components';
import {Heading, Show, Space, VStack} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';
import {useLocation, useParams} from 'react-router-dom';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetServiceOrderVariantIntegrationActionsQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';

import {useNavigate} from 'shared/web';

import {FormType} from '../../types/ServiceOrderVariant';
import {getServiceOrderVariantsRoutes} from '../../utils/getServiceOrderVariantsRoutes';

interface IntegrationTabProps {
  control: FormControl<FormType>;
  formApi: UseFormReturn<FormType>;
}

export function IntegrationTab(props: IntegrationTabProps) {
  const {id} = useParams();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const serviceOrderVariantsRoutes = getServiceOrderVariantsRoutes(pathname);

  const {
    data: integrationActions,
    isLoading,
    isError,
  } = useGetServiceOrderVariantIntegrationActionsQuery(
    {serviceOrderVariantId: id ?? ''},
    {skip: isNilOrEmpty(id)}
  );

  return (
    <>
      <SettingsSection>
        <DataStatus
          isLoading={isLoading}
          isError={isError}
          isEmpty={isNilOrEmpty(integrationActions?.actions)}
        >
          <VStack spacing={6}>
            <Show when={integrationActions?.actions.includes('AUTO_SEND_TO_CHRONOS')}>
              <Heading size={4}>{i18n.t('entity.orderIntegration.labels.chronos')}</Heading>
              <FormField
                control={props.control}
                label={i18n.t('entity.serviceOrder.labels.automaticallySendToChronos')}
                name="integration.isAutomaticSendToChronos"
                type="switch"
                data-testid={testIds.settings.serviceOrderVariantsDetail(
                  'isAutomaticSendToChronos'
                )}
              />
            </Show>
            <Show when={integrationActions?.actions.includes('SET_AS_DEFAULT_FOR_XENTRY')}>
              <Heading size={4}>{i18n.t('entity.orderIntegration.labels.xentryOrder')}</Heading>
              <FormField
                control={props.control}
                label={i18n.t('entity.serviceOrder.labels.defaultForXentryServiceMeasures')}
                name="integration.isDefaultForXentryServiceMeasures"
                type="switch"
                data-testid={testIds.settings.serviceOrderVariantsDetail(
                  'isDefaultForXentryServiceMeasures'
                )}
              />
              <FormField
                control={props.control}
                label={i18n.t('entity.serviceOrder.labels.excludeFromXentrySynchronization')}
                name="integration.isExcludedFromXentrySynchronization"
                type="switch"
                data-testid={testIds.settings.serviceOrderVariantsDetail(
                  'isExcludedFromXentrySynchronization'
                )}
              />
            </Show>
          </VStack>
        </DataStatus>
      </SettingsSection>
      <Space vertical={20} />
      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: i18n.t('general.actions.discardChanges'),
            onClick: () => navigate(serviceOrderVariantsRoutes.overview),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: i18n.t('general.actions.saveChanges'),
          },
        ]}
        data-testid={testIds.settings.serviceOrderVariantsDetail('footer')}
      />
    </>
  );
}
